import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import { Col, Form, } from 'antd';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { PASSENGER_FLOW_MODE, PASSENGER_FLOW_MODE_MAP, PASSENGER_FLOW_SPOTS, PASSENGER_FLOW_WEEKEND, PASSENGER_FLOW_WEEKDAY, PASSENGER_FLOW_SPOT_LIST, SPOTS_ANALYSIS_MAP } from './config';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import SpotFlowCard from './SpotFlowCard';
import V2FormInputNumber from '@/common/components/Form/V2FormInputNumber/V2FormInputNumber';
// import dayjs from 'dayjs';
import { dateFns, debounce, isArray, isNotEmptyAny, isUndef } from '@lhb/func';
import styles from './index.module.less';
import { useMethods } from '@lhb/hook';
import { ControlType, setComValue } from '../../config';
import { addressIdentification } from '../../customize';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { getTaskAnalysisList, getTaskSpotSimplePage } from '@/common/api/expandStore/chancepoint';

/**
 * @Description 客流计数
 */
const PassengerFlowCounting:FC<{
  form: any;
  propertyItem?: any;
  disabled?: boolean;
  required?: boolean;
  tiledFormDataRef?: any;
  isEdit?: boolean;
}> = ({
  form,
  disabled,
  required,
  tiledFormDataRef,
  propertyItem,
  isEdit
}) => {
  const passengerFlowMode = Form.useWatch(PASSENGER_FLOW_MODE.key, form);
  const passengerFlowSpots = Form.useWatch(PASSENGER_FLOW_SPOTS.key, form);
  const [spotsList, setSpotsList] = useState<any>([]); // 客流点位信息列表
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  // 编辑模式首次加载标记，用于 handleSpotsChange 中跳过自动计算客流值，避免覆盖用户自定义编辑的值
  const isFirstSpotsLoadRef = useRef<boolean>(!!isEdit);
  const [propertyItemValue, setPropertyItemValue] = useState<any>({});
  const [selections, setSelections] = useState<any>({
    modeOptions: [
      { label: '设备采集', value: 1 },
      { label: '人工采集', value: 2 },
    ],
    spotsOptions: [],
  });

  // 当前表单的机会点经纬度表标识
  const identificationFormItemLngLat = useMemo(() => {
    const addressItem = tiledFormDataRef?.current?.find((item) => item?.controlType === ControlType.ADDRESS.value);
    return `${addressItem?.identification || addressIdentification}-lnglat`;
  }, [tiledFormDataRef?.current]);

  const lnglat = Form.useWatch(identificationFormItemLngLat, form);

  useEffect(() => {
    if (isUndef(propertyItem?.textValue) || typeof (propertyItem?.textValue) !== 'string') return;
    setPropertyItemValue(JSON.parse(propertyItem?.textValue || '{}'));
  }, [propertyItem]);

  useEffect(() => {
    if (!lnglat) return;

    methods.reloadSpots();

  }, [lnglat]);

  // 可编辑态下点位都要从接口取最新数据
  useEffect(() => {
    methods.handleSpotsChange();
  }, [passengerFlowSpots]);

  const methods = useMethods({
    /** 刷新点位列表 */
    reloadSpots: debounce(() => {

      if (disabled) {
        setSelections(state => ({ ...state, spotsOptions: propertyItemValue?.[PASSENGER_FLOW_SPOT_LIST] || [] }));
        return;
      }

      const [lng, lat] = lnglat?.split(',');

      // 和移动端保持同步，只取前20条数据
      getTaskSpotSimplePage({
        longitude: lng,
        latitude: lat,
        size: 20,
        page: 1,
        taskAnaStatus: SPOTS_ANALYSIS_MAP.ANALYSISED,
        distance: 300 }).then((res) => {
        setSelections(state => ({ ...state, spotsOptions: res?.objectList || [] }));
      });

      if (isEdit && isFirstLoad) {
        setIsFirstLoad(false);
        return;
      }
      if (passengerFlowMode === PASSENGER_FLOW_MODE_MAP.DEVICE) {
        // 经纬度变化时清空点位及客流数据
        form.setFieldsValue({
          [PASSENGER_FLOW_SPOTS.key]: [],
          [PASSENGER_FLOW_WEEKDAY.key]: null,
          [PASSENGER_FLOW_WEEKEND.key]: null,
        });
      }
    }, 200),
    /** 客流计数模式切换 */
    handleModeChange(e: any) {
      if (e?.target?.value === PASSENGER_FLOW_MODE_MAP.OPERATION || (e?.target?.value === PASSENGER_FLOW_MODE_MAP.DEVICE && !isNotEmptyAny(passengerFlowSpots))) {
        form.setFieldsValue({
          [PASSENGER_FLOW_WEEKDAY.key]: null,
          [PASSENGER_FLOW_WEEKEND.key]: null,
        });
      }
      this.updateValue(PASSENGER_FLOW_MODE.key, e?.target?.value);
    },
    /** 点位选择变化 */
    handleSpotsChange() {
      // 禁用状态下用快照数据
      if (disabled) {
        if (isNotEmptyAny(propertyItemValue)) {
          setSpotsList(propertyItemValue?.[PASSENGER_FLOW_SPOT_LIST] || []);
        }
        return;
      }
      if (!isNotEmptyAny(passengerFlowSpots)) {
        if (passengerFlowMode === PASSENGER_FLOW_MODE_MAP.DEVICE) {
          setSpotsList([]);
          this.updateValue(PASSENGER_FLOW_SPOTS.key, []);
          form.setFieldsValue({
            [PASSENGER_FLOW_WEEKDAY.key]: null,
            [PASSENGER_FLOW_WEEKEND.key]: null,
          });
        }
        return;
      }
      getTaskAnalysisList({ taskAnaStatus: SPOTS_ANALYSIS_MAP.ANALYSISED, ids: passengerFlowSpots }).then((res) => {
        setSpotsList(res || []);
        this.updateValue(PASSENGER_FLOW_SPOTS.key, res || []);
        // 编辑模式首次加载时，若表单已有保存的客流值（可能是用户自定义编辑的），跳过自动计算避免覆盖
        // 若表单中没有已保存的客流值（如历史数据），仍需执行自动计算
        if (isFirstSpotsLoadRef.current) {
          isFirstSpotsLoadRef.current = false;
          const weekdayVal = form.getFieldValue(PASSENGER_FLOW_WEEKDAY.key);
          const weekendVal = form.getFieldValue(PASSENGER_FLOW_WEEKEND.key);
          if (!isNotEmptyAny(weekdayVal) && !isNotEmptyAny(weekendVal)) {
            this.getCountBySpots(res || []);
          }
        } else {
          this.getCountBySpots(res || []);
        }
      });

    },
    updateValue(key: any, value: any) {
      if (key === PASSENGER_FLOW_SPOTS.key) {
        setComValue(propertyItem, {
          [PASSENGER_FLOW_SPOTS.key]: passengerFlowSpots,
          // 点位卡片快照数据-详情展示用
          passengerFlowSpotList: value,
        });
      } else {
        setComValue(propertyItem, {
          [key]: value
        });
      }
    },
    /** 获取所选点位最高周中客流和周末客流 */
    getCountBySpots(list) {

      let allDataArr: any = [];

      list?.forEach((item: any) => {
        allDataArr = allDataArr.concat(item.deviceTaskAnalysisList);
      });

      const weekdayArr: any = [];
      const weekendArr: any = [];

      allDataArr.forEach((item) => {
        dateFns.getIsWeekend(item.date) ? weekendArr.push(item) : weekdayArr.push(item);
      });

      const weekdayMaxCount = weekdayArr.length ? Math.max(...weekdayArr?.map((item) => item.passbyCount)) : 0;
      const weekendMaxCount = weekendArr.length ? Math.max(...weekendArr?.map((item) => item.passbyCount)) : 0;

      form.setFieldsValue({
        [PASSENGER_FLOW_WEEKDAY.key]: weekdayMaxCount,
        [PASSENGER_FLOW_WEEKEND.key]: weekendMaxCount
      });
      setComValue(propertyItem, {
        [PASSENGER_FLOW_WEEKDAY.key]: weekdayMaxCount,
        [PASSENGER_FLOW_WEEKEND.key]: weekendMaxCount
      });
    }
  });

  return (
    <>
      {/* 统计模式 */}
      <Col span={12}>
        <V2FormRadio
          label={PASSENGER_FLOW_MODE.label}
          name={PASSENGER_FLOW_MODE.key}
          required={required}
          disabled={disabled}
          options={selections.modeOptions}
          onChange={methods.handleModeChange}/>
      </Col>

      {passengerFlowMode === PASSENGER_FLOW_MODE_MAP.DEVICE
        ? <>
          {/* 客流点位 */}
          <Col span={12}>
            <V2FormSelect
              label={PASSENGER_FLOW_SPOTS.label}
              name={PASSENGER_FLOW_SPOTS.key}
              options={selections?.spotsOptions || []}
              mode='multiple'
              required={required}
              disabled={disabled}
              config={{
                getPopupContainer: node => node.parentNode,
                fieldNames: { label: 'name', value: 'id' },
                onClick: () => { !lnglat && V2Message.info('请先选择地址'); }
              }}/>
          </Col>
          {/* 客流点位卡片 */}
          {isArray(spotsList) && spotsList.length ? <Col span={24}>
            <div className={styles.spotFlowList}>
              {spotsList?.map((item, index) => (
                <SpotFlowCard key={index} itemData={item}/>
              ))}
            </div>
          </Col> : <></>}
        </>
        : <></>}

      {!isUndef(passengerFlowMode) ? <>
        {/* 周中客流 */}
        <Col span={12}>
          <V2FormInputNumber
            label={PASSENGER_FLOW_WEEKDAY.label}
            name={PASSENGER_FLOW_WEEKDAY.key}
            required={required}
            config={{ addonAfter: '人' }}
            precision={0}
            // disabled={disabled || passengerFlowMode === PASSENGER_FLOW_MODE_MAP.DEVICE}
            // 甜瓜说改成可自定义
            disabled={disabled}
            onChange={(val) => methods.updateValue(PASSENGER_FLOW_WEEKDAY.key, val)}/>
        </Col>
        {/* 周末客流 */}
        <Col span={12}>
          <V2FormInputNumber
            label={PASSENGER_FLOW_WEEKEND.label}
            name={PASSENGER_FLOW_WEEKEND.key}
            precision={0}
            required={required}
            config={{ addonAfter: '人' }}
            // disabled={disabled || passengerFlowMode === PASSENGER_FLOW_MODE_MAP.DEVICE}
            disabled={disabled}
            onChange={(val) => methods.updateValue(PASSENGER_FLOW_WEEKEND.key, val)}/>
        </Col>
      </> : <></>}
    </>
  );
};

export default PassengerFlowCounting;
